import { useCallback, useMemo } from 'react';

import {
  array,
  flow,
  nonEmptyArray,
  not,
  option,
  panic,
  pipe,
  remote,
  task,
} from '@code-expert/prelude';
import type { UserId } from '/imports/domain';
import { invokeQuery } from '/imports/ui/hooks/useProcedure';
import { useSafeState } from '/imports/ui/hooks/useSafeState';

export interface UserNameInterface {
  _id: UserId;
  userName: string;
  email: string;
  userTitleName: string;
}

/**
 * In-memory cache of username data
 */
const cache = new Map<UserId, remote.Remote<UserNameInterface>>();

const exists = (id: UserId): boolean => cache.has(id);

/**
 * Hook to improve performance of getting the userName by using an in-memory cache
 */
export const useUserNameCache = () => {
  const [cacheState, setCacheState] = useSafeState(cache.size);

  /**
   * Manages fetching usernames and stores the result in the cache
   */
  const fillCache = useCallback(
    (userIds: Array<UserId>): void => {
      const updateCache = (usernames: Array<UserNameInterface>): void => {
        usernames.forEach((u) => cache.set(u._id, remote.done(u)));
        if (!userIds.every((id) => cache.has(id))) {
          throw new Error('Not all requested userIds are found in the cache', {
            cause: { missingUserIds: userIds.filter((id) => !cache.has(id)) },
          });
        }
        setCacheState(cache.size);
      };

      void pipe(
        userIds,
        array.filter(not(exists)),
        nonEmptyArray.fromArray,
        option.traverse(task.ApplicativePar)(
          flow(
            nonEmptyArray.map((id) => (cache.set(id, remote.pending), id)),
            invokeQuery('user_getNames'),
            task.chainFirstVoid(updateCache),
          ),
        ),
        task.run,
      );
    },
    [cacheState],
  );

  const makeLookup =
    (userIds: Array<UserId>) =>
    (id: UserId): remote.Remote<UserNameInterface> => {
      fillCache(userIds);
      // After filling the cache, lookups for a userId must succeed
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return cache.get(id)!;
    };

  /**
   * Find all usernames for a list of user IDs
   */
  const getUsersFromCache = useCallback(
    (userIds: Array<UserId>) =>
      pipe(userIds, array.traverse(remote.Applicative)(makeLookup(userIds))),
    [fillCache],
  );

  /**
   * Find the username for a specific user ID
   */
  const getUserFromCache = useCallback(
    (userId: UserId) =>
      pipe(
        getUsersFromCache([userId]),
        remote.map(
          flow(
            array.head,
            option.getOrElse<UserNameInterface>(() =>
              panic('Failed to lookup username', { userId }),
            ),
          ),
        ),
      ),
    [getUsersFromCache],
  );

  return useMemo(
    () => ({ getUserFromCache, getUsersFromCache, fillCache }),
    [getUserFromCache, getUsersFromCache, fillCache],
  );
};
